import { httpService } from "./HttpService";
import ApiPaths from "./ApiPaths";
import Session from "../models/Session";
import { convertStrDateUTCToStrDateGB } from "../utils/Utils";
import { PageResponse } from "../models/PageResponse";
import { AnalyseResponse, CreateFromAnalysisRequest } from "../models/AnalyseResponse";
import { ItemSearchResult } from "../models/ItemSearchResult";
import { SessionItemEditRequest } from "../models/SessionItemEditRequest";
import { ItemEditHistEntry } from "../models/ItemEditHistEntry";
import SessionItem from "../models/SessionItem";

export const createSession = async (formData: FormData) => {
    try {
        const response = await httpService.post<Session>(ApiPaths.CREATE_SESSIONS, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });

        response.data.createdDate = convertStrDateUTCToStrDateGB(response.data.createdDate);
        return response.data;
    } catch (error) {
        console.error('Error creating session:', error);
        throw error;
    }
}

export const listAllSessions = async (urlParams: string) => {
  try {
    const response = await httpService.get<PageResponse<Session>>(
      `${ApiPaths.GET_ALL_SESSIONS}?${urlParams}`
    );

    // Format createdDate in all sessions
    response.data.content.forEach(session => {
      session.createdDate = convertStrDateUTCToStrDateGB(session.createdDate);
    });

    return response.data;
  } catch (error) {
    console.error("Error fetching sessions:", error);
    throw error;
  }
};

export const getSessionById = async (id: string) => {
    try {
        const response = await httpService.get<Session>(`${ApiPaths.SESSIONS}/${id}`);
        response.data.createdDate = convertStrDateUTCToStrDateGB(response.data.createdDate);
        response.data.closedDate = response.data.closedDate ? convertStrDateUTCToStrDateGB(response.data.closedDate) : 'N/A';
        response.data.closedBy = response.data.closedBy || 'N/A';
        response.data.closedByName = response.data.closedByName || 'N/A';
        return response.data;
    } catch (error) {
        console.error('Error fetching session by ID:', error);
        throw error;
    }
}

export const deleteSession = async (id: string) => {
    try {
        await httpService.delete(`${ApiPaths.SESSIONS}/${id}`);
    } catch (error) {
        console.error('Error deleting session:', error);
        throw error;
    }
}

export const closeSession = async (id: string) => {
    try {
        await httpService.put(`${ApiPaths.SESSIONS}/${id}/close`, {});
    } catch (error) {
        console.error('Error closing session:', error);
        throw error;
    }
}

export const postSession = async (id: string) => {
    try {
        await httpService.put(`${ApiPaths.SESSIONS}/${id}/post`, {});
    } catch (error) {
        console.error('Error posting session:', error);
        throw error;
    }
}

export const analyseFiles = async (formData: FormData): Promise<AnalyseResponse> => {
    const response = await httpService.post<AnalyseResponse>(ApiPaths.SESSIONS_ANALYSE, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
};

export const createFromAnalysis = async (request: CreateFromAnalysisRequest): Promise<any[]> => {
    const response = await httpService.post<any[]>(ApiPaths.SESSIONS_CREATE_FROM_ANALYSIS, request);
    return response.data;
};

export interface ItemSearchPageResponse {
    content: ItemSearchResult[];
    page: number;
    size: number;
    totalCount: number;
    hasMore: boolean;
}

export type ItemSearchSort = 'PART_ASC' | 'DATE_DESC' | 'DATE_ASC';

export const searchItems = async (params: {
    partNumber?: string;
    statusFilter?: 'NOT_OK' | 'NOT_OK_PART' | 'PART';
    sessionId?: string;
    fromDate?: string;
    toDate?: string;
    sort?: ItemSearchSort;
    page?: number;
    size?: number;
}): Promise<ItemSearchPageResponse> => {
    const query = new URLSearchParams();
    if (params.partNumber) query.append('partNumber', params.partNumber);
    if (params.statusFilter) query.append('statusFilter', params.statusFilter);
    if (params.sessionId) query.append('sessionId', params.sessionId);
    if (params.fromDate) query.append('fromDate', params.fromDate);
    if (params.toDate) query.append('toDate', params.toDate);
    if (params.sort) query.append('sort', params.sort);
    if (params.page !== undefined) query.append('page', String(params.page));
    if (params.size !== undefined) query.append('size', String(params.size));

    const response = await httpService.get<ItemSearchPageResponse>(
        `${ApiPaths.SESSIONS_ITEMS_SEARCH}?${query.toString()}`
    );
    return response.data;
};

export const editItem = async (
    sessionId: string,
    itemId: string,
    request: SessionItemEditRequest
): Promise<SessionItem> => {
    const response = await httpService.put<SessionItem>(
        `${ApiPaths.SESSIONS}/${sessionId}/items/${itemId}`,
        request
    );
    return response.data;
};

export const getItemHistory = async (
    sessionId: string,
    itemId: string
): Promise<ItemEditHistEntry[]> => {
    const response = await httpService.get<ItemEditHistEntry[]>(
        `${ApiPaths.SESSIONS}/${sessionId}/items/${itemId}/history`
    );
    return response.data;
};